import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { productService } from '@/api/services/product'
import { useRequest } from '@/composables/useRequest'
import { haptic } from '@/composables/useTelegram'
import { useMenuStore } from '@/stores/menu'
import { useUserStore } from '@/stores/user'
import { toProduct } from '@/utils/product'

/**
 * Избранное (§12 LOGIC.md). Бэкенд хранит только id, поэтому цены и названия
 * всегда берём из текущего меню — в другом городе избранное может не продаваться.
 */
export const useFavoritesStore = defineStore('favorites', () => {
  const userStore = useUserStore()
  const menuStore = useMenuStore()

  const ids = ref<string[]>([])

  /* Тихий: пустое избранное — норма, пугать клиента ошибкой не за что */
  const listRequest = useRequest(productService.favorites, { silent: true })
  const toggleRequest = useRequest(productService.toggleFavorite)

  /** Товары для экрана: те, что ещё есть в меню. */
  const products = computed(() =>
    ids.value.flatMap((id) => {
      const product = menuStore.productsEnum[id]
      return product ? [toProduct(product)] : []
    }),
  )

  const isFavorite = (productId: string): boolean => ids.value.includes(productId)

  async function load(): Promise<void> {
    if (!userStore.isAuthorized) return
    ids.value = (await listRequest.execute(userStore.userId)) ?? []
  }

  function apply(productId: string, value: boolean): void {
    ids.value = value
      ? [...ids.value, productId]
      : ids.value.filter((id) => id !== productId)
  }

  /**
   * Сердце закрашиваем сразу, ответа не ждём: тап должен откликаться мгновенно,
   * а запрос идёт через туннель и может думать секунду. Отказал бэкенд —
   * возвращаем как было. Смотрим на `error`, а не на ответ: тоггл отдаёт
   * пустое тело, и успех от ошибки по нему не отличить.
   */
  async function toggle(productId: string): Promise<void> {
    if (!userStore.isAuthorized) return

    const next = !isFavorite(productId)
    apply(productId, next)
    /* Отклик вместе с закраской сердца, а не по ответу: ждать секунду нельзя */
    haptic.impact()

    await toggleRequest.execute({ productId, customerId: userStore.userId, isFavorite: next })
    if (toggleRequest.error.value) apply(productId, !next)
  }

  /** Отдельной ручки «очистить» у бэкенда нет — снимаем по одному. */
  /*
   * Избранное тоже про клиента: после выхода сердца предыдущего остались бы
   * гореть на карточках до перезагрузки. Новому клиенту сразу тянем его.
   */
  watch(
    () => userStore.userId,
    (id, previous) => {
      if (!previous || id === previous) return

      ids.value = []
      if (id) void load()
    },
  )

  async function clear(): Promise<void> {
    await Promise.all(ids.value.map((id) => toggle(id)))
  }

  return { ids, products, isFavorite, load, toggle, clear }
})
